import { useState, useCallback, useRef, useEffect } from 'react'
import {
  generateShots,
  groupShotsToScenes
} from '../../../services/api/aiVideoGeneration'
import { useTaskPoller } from './useTaskPoller'
import { normalizeSceneAudioHistory } from '../utils/audioHistory'

const MAX_RETRIES = 10
const POLL_INTERVAL = 15000

/**
 * Custom hook to manage shot generation and scene mapping
 * Handles fetching shots, polling status, and formatting scene data
 */
export const useShotsGeneration = ({
  formData,
  setFormData,
  onLocationMapGenerate,
  onAudioClustering,
  parseJSONSafely,
  videoId
} = {}) => {
  const isFetchingShots = useRef(false)
  const [shotsGenerationProgress, setShotsGenerationProgress] = useState(0)
  const [isClustering, setIsClustering] = useState(false)
  const [clusteringProgress, setClusteringProgress] = useState(0)
  const shotsPoller = useTaskPoller({
    pollInterval: 2000,
    continuePollingOnError: false
  })
  const clusteringPoller = useTaskPoller({
    pollInterval: 15000,
    continuePollingOnError: false
  })

  /**
   * Format scene object from API response
   */
  const formatSceneObj = useCallback(sceneObj => {
    return Object.entries(sceneObj)
      .map(([sceneKey, sceneValue]) => {
        const { scene_location = '', shots = [] } = sceneValue

        // Normalize shots to a consistent array of { key, value }
        let shotsToProcess = []
        if (Array.isArray(shots)) {
          shotsToProcess = shots.map(s => {
            const k = Object.keys(s)[0]
            return { key: k, value: s[k] }
          })
        } else if (typeof shots === 'object' && shots !== null) {
          shotsToProcess = Object.entries(shots).map(([k, v]) => ({
            key: k,
            value: v
          }))
        }

        const formattedShots = shotsToProcess.map(
          ({ key: shotKey, value: shotValue }, index) => {
            return {
              ...shotValue,
              id: `${sceneKey}-shot-${index + 1}`,
              key: shotKey,
              title: shotValue.title || '',
              initial_image_prompt:
                shotValue['initial image prompt'] ||
                shotValue.initial_image_prompt ||
                '',
              scenery: shotValue.scenery || '',
              shot_direction:
                shotValue['shot direction'] || shotValue.shot_direction || '',
              video_prompt:
                shotValue['video prompt'] || shotValue.video_prompt || '',
              voiceover_text:
                shotValue['voiceover text'] || shotValue.voiceover_text || '',
              lighting: shotValue.lighting || '',
              dialogue: shotValue.dialogue || {},
              characters: shotValue.characters || [],
              video_duration: shotValue.video_duration || '',
              vidSrc: shotValue.video_url || shotValue.vidSrc || ''
            }
          }
        )

        return {
          id: sceneKey,
          title: sceneKey,
          description: scene_location,
          shots: formattedShots,
          aggregated_voiceover: sceneValue.aggregated_voiceover || '',
          audio_duration: sceneValue.audio_duration || 0,
          audio_duration_estimated:
            sceneValue.audio_duration_estimated || false,
          estimated_audio_duration: sceneValue.estimated_audio_duration || null,
          audio_link: sceneValue.audio_link || '',
          audio_history: sceneValue.audio_history || [],
          scene_location: scene_location
        }
      })
      .map(normalizeSceneAudioHistory)
  }, [])

  const updateFormDataWithShots = useCallback(
    (sceneData, shotsData) => {
      const formattedSceneData = formatSceneObj(sceneData)

      setFormData(prev => ({
        ...prev,
        shotList: formattedSceneData,
        shotsApiRes: shotsData,
        ...(shotsData.location_map && {
          locationMap: parseJSONSafely?.(shotsData.location_map)
        }),
        ...(shotsData.character_map && {
          characterMap: parseJSONSafely?.(shotsData.character_map)
        }),
        ...(shotsData.outfit_map && {
          outfitMap: parseJSONSafely?.(shotsData.outfit_map)
        }),
        ...(shotsData.prop_map && {
          propMap: parseJSONSafely?.(shotsData.prop_map)
        }),
        ...(shotsData.props && {
          props: parseJSONSafely?.(shotsData.props)
        }),
        ...(shotsData.wardrobe && {
          wardrobe: parseJSONSafely?.(shotsData.wardrobe)
        })
      }))

      return formattedSceneData
    },
    [formatSceneObj, parseJSONSafely, setFormData]
  )

  /**
   * Handle scene mapping from shot data
   */
  const handleScenesMapping = useCallback(
    async (data, isFineTuned) => {
      try {
        const payload = {
          input_data: data,
          video_id: videoId
        }

        setIsClustering(true)
        setClusteringProgress(0)

        const response = await groupShotsToScenes(payload)

        if (response.data && response.data.task_id) {
          const result = await clusteringPoller.startPolling(
            response.data.task_id
          )

          if (result && result.status === 'success' && result.output) {
            const formattedSceneData = updateFormDataWithShots(
              result.output,
              data
            )
            setClusteringProgress(100)

            if (onAudioClustering && formData?.selectedVoice) {
              await onAudioClustering(result.output, data)
            }

            return formattedSceneData
          }
        } else {
          const sceneData = response.data && response.data.output
          const formattedSceneData = updateFormDataWithShots(sceneData, data)

          console.log('from shots', data)

          if (onAudioClustering && formData?.selectedVoice) {
            await onAudioClustering(sceneData, data)
          }
        }
      } catch (error) {
        console.error('Scene mapping failed:', error)
      } finally {
        setFormData(prev => ({ ...prev, shotLoading: false }))
        setIsClustering(false)
        setClusteringProgress(0)
      }
    },
    [
      clusteringPoller,
      formData?.selectedVoice,
      onAudioClustering,
      setFormData,
      updateFormDataWithShots,
      videoId
    ]
  )

  /**
   * Poll shots task status
   */
  const pollShotsTaskStatus = useCallback(
    async taskId => {
      if (!isFetchingShots.current) return

      const result = await shotsPoller.startPolling(taskId)

      if (result && result.status === 'success' && result.output) {
        setShotsGenerationProgress(100)
        await handleScenesMapping(result.output)
      }
    },
    [handleScenesMapping, shotsPoller]
  )

  /**
   * Fetch shots from voiceover text
   */
  const fetchShots = useCallback(
    async (retryStyleId = null, retryCount = 0) => {
      // Handle legacy retryCount as first argument or styleId
      const styleIdArg = typeof retryStyleId === 'string' ? retryStyleId : null
      const actualRetryCount =
        typeof retryStyleId === 'number' ? retryStyleId : retryCount

      if (actualRetryCount === 0) {
        if (isFetchingShots.current) return
        isFetchingShots.current = true
      }

      setFormData(prev => ({ ...prev, shotLoading: true }))
      const storyText = formData && formData.voiceoverText
      // Shot generation must use the selected shot style id (not video style key).
      let styleId =
        styleIdArg ||
        formData?.shotGenerationStyle?.id ||
        formData?.shotGenerationStyle?.raw?.id ||
        formData?.shotGenerationStyle?.key ||
        (typeof formData?.shotGenerationStyle === 'string'
          ? formData.shotGenerationStyle
          : null) ||
        null

      if (styleId === 'default_style') styleId = null

      if (!storyText) {
        console.error('fetchShots called with empty storyText')
        setFormData(prev => ({ ...prev, shotLoading: false }))
        if (actualRetryCount === 0) isFetchingShots.current = false
        return
      }

      try {
        const res = await generateShots({
          story: storyText,
          style_id: styleId,
          video_id: videoId,
          video_mode: formData.audioMode,
          language: formData?.language || 'en',
          video_artistic_style:
            formData?.videoArtisticStyle === null
              ? 'realistic'
              : formData?.videoArtisticStyle
        })
        const taskId = res && res.data && res.data.task_id

        if (taskId) {
          await pollShotsTaskStatus(taskId)
        } else {
          const shotsData = res && res.data
          await handleScenesMapping(shotsData)
        }
      } catch (err) {
        if (actualRetryCount < MAX_RETRIES) {
          setTimeout(
            () =>
              fetchShots(
                styleIdArg || actualRetryCount + 1,
                actualRetryCount + 1
              ),
            POLL_INTERVAL
          )
        } else {
          console.error('Error generating shots:', err)
          setFormData(prev => ({ ...prev, shotLoading: false }))
        }
      } finally {
        if (actualRetryCount === 0) isFetchingShots.current = false
      }
    },
    [
      formData?.voiceoverText,
      formData?.language,
      setFormData,
      pollShotsTaskStatus,
      handleScenesMapping,
      videoId
    ]
  )

  /**
   * Reset shots generation state
   */
  const resetShotsGeneration = useCallback(() => {
    isFetchingShots.current = false
    shotsPoller.stopPolling()
    clusteringPoller.stopPolling()
    setShotsGenerationProgress(0)
    setIsClustering(false)
    setClusteringProgress(0)
  }, [clusteringPoller, shotsPoller])

  useEffect(() => {
    setShotsGenerationProgress(shotsPoller.progress || 0)
  }, [shotsPoller.progress])

  useEffect(() => {
    setIsClustering(clusteringPoller.isPolling)
  }, [clusteringPoller.isPolling])

  useEffect(() => {
    setClusteringProgress(clusteringPoller.progress || 0)
  }, [clusteringPoller.progress])

  return {
    // State
    isFetchingShots: isFetchingShots.current,
    shotsGenerationProgress,
    isClustering,
    clusteringProgress,

    // Actions
    fetchShots,
    handleScenesMapping,
    resetShotsGeneration,

    // Utilities
    formatSceneObj
  }
}

export default useShotsGeneration
